import { copyShallow, randomString } from '../helpers';
import { ConfidentialArray, ConfidentialObjectValues } from '../helpers/confidential';
import { isFirstUnreadStatusMessage } from '../message_helpers';
function setDefault(obj, key, defaultValue) {
    if (obj[key] === undefined) {
        obj[key] = defaultValue;
    }
}
export class StringHashSet {
    setObj = {};
    val = {};
    add(str) {
        this.setObj[str] = this.val;
    }
    contains(str) {
        return this.setObj[str] === this.val;
    }
    remove(str) {
        delete this.setObj[str];
    }
    values() {
        const values = [];
        for (const i in this.setObj) {
            if (this.setObj[i] === this.val) {
                values.push(i);
            }
        }
        return values;
    }
    clearAll() {
        for (const element in this.setObj) {
            if (this.setObj.hasOwnProperty(element)) {
                this.remove(element);
            }
        }
    }
}
class Receivers {
    me = null;
    contacts = new Map();
    groups = new Map();
    distributionLists = new Map();
    get(receiverType) {
        switch (receiverType) {
            case 'me':
                return this.me;
            case 'contact':
                return this.contacts;
            case 'group':
                return this.groups;
            case 'distributionList':
                return this.distributionLists;
            default:
                throw new Error('Unknown or invalid receiver type: ' + receiverType);
        }
    }
    getData(receiver) {
        if (receiver.type === 'me') {
            return this.me.id === receiver.id ? this.me : undefined;
        }
        else {
            const receivers = this.get(receiver.type);
            return receivers.get(receiver.id);
        }
    }
    set(data) {
        this.setContacts(data['contact']);
        this.setGroups(data['group']);
        this.setDistributionLists(data['distributionList']);
    }
    setMe(data) {
        data.type = 'me';
        this.me = data;
    }
    setContacts(data) {
        this.contacts = new Map(data.map((c) => {
            c.type = 'contact';
            setDefault(c, 'color', '#f0f0f0');
            return [c.id, c];
        }));
        if (this.me !== undefined) {
            this.contacts.set(this.me.id, this.me);
        }
    }
    setGroups(data) {
        this.groups = new Map(data.map((g) => {
            g.type = 'group';
            setDefault(g, 'disabled', false);
            setDefault(g, 'locked', false);
            setDefault(g, 'visible', true);
            return [g.id, g];
        }));
    }
    setDistributionLists(data) {
        this.distributionLists = new Map(data.map((d) => {
            d.type = 'distributionList';
            return [d.id, d];
        }));
    }
    extend(receiverType, data) {
        switch (receiverType) {
            case 'me':
                return this.extendMe(data);
            case 'contact':
                return this.extendContact(data);
            case 'group':
                return this.extendGroup(data);
            case 'distributionList':
                return this.extendDistributionList(data);
            default:
                throw new Error('Unknown or invalid receiver type: ' + receiverType);
        }
    }
    extendDistributionList(data) {
        let distributionListReceiver = this.distributionLists.get(data.id);
        if (distributionListReceiver === undefined) {
            data.type = 'distributionList';
            this.distributionLists.set(data.id, data);
            return data;
        }
        distributionListReceiver = angular.extend(distributionListReceiver, data);
        return distributionListReceiver;
    }
    extendGroup(data) {
        setDefault(data, 'disabled', false);
        setDefault(data, 'locked', false);
        setDefault(data, 'visible', true);
        let groupReceiver = this.groups.get(data.id);
        if (groupReceiver === undefined) {
            data.type = 'group';
            this.groups.set(data.id, data);
            return data;
        }
        groupReceiver = angular.extend(groupReceiver, data);
        return groupReceiver;
    }
    extendMe(data) {
        if (this.me === undefined) {
            data.type = 'me';
            this.me = data;
            return data;
        }
        this.me = angular.extend(this.me, data);
        return this.me;
    }
    extendContact(data) {
        let contactReceiver = this.contacts.get(data.id);
        if (contactReceiver === undefined) {
            data.type = 'contact';
            setDefault(data, 'color', '#f0f0f0');
            this.contacts.set(data.id, data);
            return data;
        }
        contactReceiver = angular.extend(contactReceiver, data);
        return contactReceiver;
    }
}
export class Conversations {
    conversations = [];
    filter = null;
    converter = null;
    receiverService;
    constructor(receiverService) {
        this.receiverService = receiverService;
    }
    get() {
        let conversations = this.conversations;
        if (this.filter != null) {
            conversations = this.filter(conversations);
        }
        if (this.converter != null) {
            conversations = conversations.map(this.converter);
        }
        return conversations;
    }
    set(data) {
        for (const conversation of data) {
            if (conversation.position !== undefined) {
                delete conversation.position;
            }
            setDefault(conversation, 'isStarred', false);
            setDefault(conversation, 'isUnread', false);
        }
        this.conversations = data;
    }
    find(pattern) {
        for (const conversation of this.get()) {
            const a = pattern;
            const b = conversation;
            if (a !== undefined && b !== undefined && a.type === b.type && a.id === b.id) {
                return conversation;
            }
        }
        return null;
    }
    add(conversation) {
        this.conversations.splice(conversation.position, 0, conversation);
    }
    updateOrAdd(conversation, returnOld = false) {
        for (const i of this.conversations.keys()) {
            if (this.receiverService.compare(this.conversations[i], conversation)) {
                let previousConversation = null;
                if (returnOld) {
                    previousConversation = copyShallow(this.conversations[i]);
                }
                setDefault(conversation, 'isStarred', false);
                setDefault(conversation, 'isUnread', false);
                Object.assign(this.conversations[i], conversation);
                if (conversation.latestMessage === undefined
                    && this.conversations[i].latestMessage !== undefined) {
                    delete this.conversations[i].latestMessage;
                }
                if (this.conversations[i].position !== i) {
                    const tmp = this.conversations.splice(i, 1)[0];
                    this.conversations.splice(conversation.position, 0, tmp);
                }
                return previousConversation;
            }
        }
        this.add(conversation);
        return null;
    }
    remove(conversation) {
        for (const i of this.conversations.keys()) {
            if (this.receiverService.compare(this.conversations[i], conversation)) {
                this.conversations.splice(i, 1);
                return;
            }
        }
    }
    setFilter(filter) {
        this.filter = filter;
    }
    setConverter(converter) {
        this.converter = converter;
    }
}
class ReceiverMessages {
    referenceMsgId = null;
    requested = false;
    more = true;
    list = [];
}
class Messages {
    log;
    messages = new Map();
    converter = null;
    constructor(logService) {
        this.log = logService.getLogger('MessagesContainer');
    }
    lazyCreate(receiver) {
        if (!this.messages.has(receiver.type)) {
            this.messages.set(receiver.type, new Map());
        }
        const typeMap = this.messages.get(receiver.type);
        if (!typeMap.has(receiver.id)) {
            typeMap.set(receiver.id, new ReceiverMessages());
        }
    }
    getReceiverMessages(receiver) {
        this.lazyCreate(receiver);
        return this.messages.get(receiver.type).get(receiver.id);
    }
    getList(receiver) {
        return this.getReceiverMessages(receiver).list;
    }
    clear($scope) {
        this.messages.forEach((messageMap, receiverType) => {
            messageMap.forEach((messages, id) => {
                messages.requested = false;
                messages.referenceMsgId = null;
                messages.more = true;
                messages.list = [];
                this.notify({
                    id: id,
                    type: receiverType,
                }, $scope);
            });
        });
    }
    clearReceiverMessages(receiver) {
        let cachedMessageCount = 0;
        if (this.messages.has(receiver.type)) {
            const typeMessages = this.messages.get(receiver.type);
            if (typeMessages.has(receiver.id)) {
                cachedMessageCount = typeMessages.get(receiver.id).list.length;
                typeMessages.delete(receiver.id);
            }
        }
        return cachedMessageCount;
    }
    contains(receiver) {
        return this.messages.has(receiver.type) &&
            this.messages.get(receiver.type).has(receiver.id);
    }
    hasMore(receiver) {
        return this.getReceiverMessages(receiver).more;
    }
    setMore(receiver, more) {
        this.getReceiverMessages(receiver).more = more;
    }
    getReferenceMsgId(receiver) {
        return this.getReceiverMessages(receiver).referenceMsgId;
    }
    isRequested(receiver) {
        return this.getReceiverMessages(receiver).requested;
    }
    setRequested(receiver) {
        const messages = this.getReceiverMessages(receiver);
        if (messages.requested) {
            throw new Error('Message request for receiver ' + receiver.id + ' still pending');
        }
        messages.requested = true;
    }
    clearRequested(receiver) {
        const messages = this.getReceiverMessages(receiver);
        messages.requested = false;
    }
    addNewer(receiver, messages) {
        if (messages.length === 0) {
            return;
        }
        const receiverMessages = this.getReceiverMessages(receiver);
        if (receiverMessages.list.length === 0) {
            receiverMessages.referenceMsgId = messages[0].id;
        }
        for (const message of messages) {
            if (message.sortKey === undefined) {
                message.sortKey = receiverMessages.list.length > 0
                    ? receiverMessages.list[receiverMessages.list.length - 1].sortKey
                    : 0;
            }
            receiverMessages.list.push(message);
        }
    }
    addOlder(receiver, messages) {
        if (messages.length === 0) {
            return;
        }
        const receiverMessages = this.getReceiverMessages(receiver);
        const firstId = messages[0].id;
        const lastId = messages[messages.length - 1].id;
        const predicate = (msg) => msg.id === firstId || msg.id === lastId;
        if (receiverMessages.list.findIndex(predicate, receiverMessages.list) !== -1) {
            this.log.warn('Messages to be prepended intersect with existing messages:', new ConfidentialArray(messages.map((message) => new ConfidentialObjectValues(message))));
            return;
        }
        receiverMessages.referenceMsgId = messages[0].id;
        receiverMessages.list.unshift.apply(receiverMessages.list, messages);
    }
    addStatusMessage(receiver, text) {
        const receiverMessages = this.getReceiverMessages(receiver);
        receiverMessages.list.push({
            type: 'status',
            id: randomString(6),
            body: '',
            caption: text,
            sortKey: undefined,
            partnerId: receiver.id,
            isOutbox: false,
            isStatus: true,
            statusType: 'text',
        });
    }
    update(receiver, message) {
        const list = this.getList(receiver);
        for (let i = 0; i < list.length; i++) {
            if (list[i].id === message.id) {
                if (message.thumbnail === undefined) {
                    message.thumbnail = list[i].thumbnail;
                }
                list[i] = message;
                return true;
            }
        }
        return false;
    }
    setThumbnail(receiver, messageId, thumbnailImage) {
        const list = this.getList(receiver);
        for (const message of list) {
            if (message.id === messageId) {
                if (message.thumbnail === undefined) {
                    message.thumbnail = { img: thumbnailImage };
                }
                else {
                    message.thumbnail.img = thumbnailImage;
                }
                return true;
            }
        }
        return false;
    }
    remove(receiver, messageId) {
        const list = this.getList(receiver);
        for (let i = 0; i < list.length; i++) {
            if (list[i].id === messageId) {
                list.splice(i, 1);
                return true;
            }
        }
        return false;
    }
    removeTemporary(receiver, temporaryMessageId) {
        const list = this.getList(receiver);
        for (let i = 0; i < list.length; i++) {
            if (list[i].temporaryId === temporaryMessageId) {
                list.splice(i, 1);
                return true;
            }
        }
        return false;
    }
    bindTemporaryToMessageId(receiver, temporaryId, messageId) {
        const list = this.getList(receiver);
        for (const message of list) {
            if (message.temporaryId === temporaryId) {
                if (message.id !== undefined) {
                    return message;
                }
                message.temporaryId = null;
                message.id = messageId;
                return message;
            }
        }
        return null;
    }
    notify(receiver, $scope) {
        $scope.$broadcast('threema.receiver.' + receiver.type + '.' + receiver.id + '.messages', this.getList(receiver), this.hasMore(receiver));
    }
    register(receiver, $scope, callback) {
        $scope.$on('threema.receiver.' + receiver.type + '.' + receiver.id + '.messages', callback);
        return this.getList(receiver);
    }
    updateFirstUnreadMessage(receiver) {
        const receiverMessages = this.getReceiverMessages(receiver);
        if (receiverMessages !== undefined && receiverMessages.list.length > 0) {
            let firstUnreadMessageIndex;
            for (let i = receiverMessages.list.length - 1; i >= 0; i--) {
                const message = receiverMessages.list[i];
                if (isFirstUnreadStatusMessage(message)) {
                    receiverMessages.list.splice(i, 1);
                    if (firstUnreadMessageIndex !== undefined) {
                        firstUnreadMessageIndex -= 1;
                    }
                }
                else if (!message.isOutbox && message.unread) {
                    firstUnreadMessageIndex = i;
                }
            }
            if (firstUnreadMessageIndex !== undefined) {
                receiverMessages.list.splice(firstUnreadMessageIndex, 0, {
                    type: 'status',
                    isStatus: true,
                    statusType: 'firstUnreadMessage',
                });
            }
        }
    }
}
class Converter {
    static addReceiverToConversation(receivers) {
        return (conversation) => {
            conversation.receiver = receivers.getData({
                type: conversation.type,
                id: conversation.id,
            });
            return conversation;
        };
    }
}
class Typing {
    set = new StringHashSet();
    getReceiverUid(receiver) {
        return `${receiver.type}-${receiver.id}`;
    }
    setTyping(receiver) {
        this.set.add(this.getReceiverUid(receiver));
    }
    unsetTyping(receiver) {
        this.set.remove(this.getReceiverUid(receiver));
    }
    clearAll() {
        this.set.clearAll();
    }
    isTyping(receiver) {
        return this.set.contains(this.getReceiverUid(receiver));
    }
}
class Drafts {
    quotes = new Map();
    texts = new Map();
    getReceiverUid(receiver) {
        return `${receiver.type}-${receiver.id}`;
    }
    setQuote(receiver, quote) {
        this.quotes.set(this.getReceiverUid(receiver), quote);
    }
    removeQuote(receiver) {
        this.quotes.delete(this.getReceiverUid(receiver));
    }
    getQuote(receiver) {
        return this.quotes.get(this.getReceiverUid(receiver));
    }
    setText(receiver, draftMessage) {
        this.texts.set(this.getReceiverUid(receiver), draftMessage);
    }
    removeText(receiver) {
        this.texts.delete(this.getReceiverUid(receiver));
    }
    getText(receiver) {
        return this.texts.get(this.getReceiverUid(receiver));
    }
}
angular.module('3ema.container', [])
    .factory('Container', ['$filter', 'LogService', 'ReceiverService',
    function ($filter, logService, receiverService) {
        class Filters {
            static hasData(receivers) {
                return (obj) => $filter('hasData')(obj, receivers);
            }
            static hasContact(contacts) {
                return (obj) => $filter('hasContact')(obj, contacts);
            }
            static isValidMessage(contacts) {
                return (obj) => $filter('isValidMessage')(obj, contacts);
            }
        }
        return {
            Converter: Converter,
            Filters: Filters,
            createReceivers: () => new Receivers(),
            createConversations: () => new Conversations(receiverService),
            createMessages: () => new Messages(logService),
            createTyping: () => new Typing(),
            createDrafts: () => new Drafts(),
        };
    },
]);
